import { Component, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { mixed, reach } from 'yup';
import { produce } from 'immer';
import {
    bindAll,
    debounce,
    get,
    getOr,
    isEqual,
    isFunction,
    isNil,
    isString,
    noop,
    omit,
    pickBy,
    isEmpty,
    identity,
    isObject,
    toPath,
    mapKeys
} from 'lodash/fp';
import { compare, set } from 'app/lib/utils';

const OWN_PROPS = [
    'onSubmit',
    'onUpdateSummary',
    'schema',
    'initialValues',
    'isInitialValid',
    'isInitialDirty',
    'resetToInitialValuesOnSubmit',
    'allowTransformation'
];

export const FormContext = createContext();

export const useFormContext = () => useContext(FormContext);

const getOrEmptyString = getOr('');

function areVisibleFieldsValid(visibleFields, invalid) {
    return visibleFields.every((name) => !get(name, invalid));
}

const getInitialState = ({ isInitialValid, initialValues, isInitialDirty }) => ({
    isFormValid: isInitialValid,
    isPristine: !isInitialDirty,
    values: { ...initialValues },
    invalid: {},
    touched: {},
    errors: {},
    pristine: {},
    visible: {},
    labels: {}
});

const getInitialFieldValue = (name) => (props) => get(['initialValues', ...toPath(name)], props);

const isInitialValue = (name, value) => (props) =>
    value === getOrEmptyString(['initialValues', ...toPath(name)], props);

export class Form extends Component {
    constructor(props) {
        super(props);

        this._isMounted = false;

        this.state = getInitialState(props);

        bindAll(
            [
                'getFieldValue',
                'getFieldError',
                'getFieldSchema',
                'setFieldValue',
                'setFieldTouched',
                'setFieldLabel',
                'setIsFormValid',
                'setFieldVisible',
                'resetField',
                'resetAllFields',
                'handleSubmit'
            ],
            this
        );

        this.runDebouncedValidation = debounce(300, this.validate.bind(this));
    }

    componentDidMount() {
        this._isMounted = true;

        if (this.props.isInitialValid) {
            return;
        }

        this.validate();
    }

    UNSAFE_componentWillReceiveProps(next) {
        if (next.schema !== this.props.schema) {
            this.generateSummaryIfRequired();
        }
        if (!isEqual(next.initialValues, this.props.initialValues)) {
            this.runDebouncedValidation();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.runDebouncedValidation.cancel();
    }

    handleSubmit(event) {
        event.preventDefault();

        const { isFormValid } = this.state;

        const {
            onSubmit,
            resetToInitialValuesOnSubmit,
            isInitialDirty,
            isSubmitting,
            initialValues
        } = this.props;

        const isPristine = !isInitialDirty && isEqual(this.state.values, this.props.initialValues);

        if (!isFormValid || isSubmitting || isPristine) {
            return null;
        }

        const transformedValues = this.props.allowTransformation
            ? this.props.schema.cast(this.state.values)
            : this.state.values;

        onSubmit(transformedValues);

        if (resetToInitialValuesOnSubmit) {
            this.setState(() => ({
                values: initialValues,
                touched: {}
            }));
        }
    }

    getFieldValue(name) {
        return get(name, this.state.values);
    }

    getFieldError(name) {
        const error = get(name, this.state.errors);
        return isNil(error) ? error : this.rewriteErrorWithLabels(error, name);
    }

    // Find the label of the field registered at `path`, or, for paths below a
    // registered field (e.g. composite values), the label of the deepest field
    // containing `path`.
    getFieldLabel(path) {
        let match = null;

        for (const name in this.state.labels) {
            if (
                (path === name || path.startsWith(`${name}.`) || path.startsWith(`${name}[`)) &&
                (match === null || name.length > match.length)
            ) {
                match = name;
            }
        }

        return match === null ? undefined : this.state.labels[match];
    }

    getFieldSchema(name) {
        try {
            return reach(this.props.schema, name);
        } catch {
            return undefined;
        }
    }

    getIsFieldTouched(name) {
        return getOr(false, name, this.state.touched);
    }

    getIsFieldValid(name) {
        // This will either be `true` indicating the field has an error, or
        // an object, indicating a subfield has an error, in either case the
        // value is truthy if it's invalid.
        const isInvalid = Boolean(get(name, this.state.invalid));
        return !isInvalid;
    }

    setFieldValue(name, value) {
        if (!this._isMounted) {
            return;
        }

        const isFieldInitialValue = isInitialValue(name, value);
        this.setState(
            produce((draft) => {
                set(name, value, draft.values);
                set(name, isFieldInitialValue(this.props), draft.pristine);
            }),
            this.runDebouncedValidation
        );
    }

    setFieldTouched(name) {
        if (!this._isMounted) {
            return;
        }
        this.setState(
            produce((draft) => {
                set(name, true, draft.touched);
            }),
            this.runDebouncedValidation
        );
    }

    setIsFormValid(isFormValid, flattened = {}) {
        if (!this._isMounted) {
            return;
        }

        // Yup Schema
        if (isFunction(this.props.schema.validate)) {
            // Ugly hack to put quotes back in to the key inside parens. We need this to pass
            // a valid path to lodash.toPath for `set` to work.
            flattened = mapKeys(
                (key) => key.replace(/\[(?![\d"'])/g, '["').replace(/(?<![\d"'])\]/g, '"]'),
                flattened
            );
        }

        const isTouchedOrNonEmpty = (_, key) => {
            const value = this.getFieldValue(key);
            const touched = this.getIsFieldTouched(key);
            return touched || !isEmpty(value);
        };

        const errorsOnTouchedFields = pickBy(isTouchedOrNonEmpty, flattened);

        const sortedPaths = Object.keys(errorsOnTouchedFields).sort(compare(identity, -1));

        const errors = {};

        for (const path of sortedPaths) {
            const currentValue = get(path, errors);
            const message = errorsOnTouchedFields[path];
            const newValue = isObject(currentValue)
                ? { __message__: message, ...currentValue }
                : message;

            set(path, newValue, errors);
        }

        const invalid = {};
        for (const path in flattened) {
            set(path, true, invalid);
        }

        const isPristine =
            !this.props.isInitialDirty && isEqual(this.state.values, this.props.initialValues);

        this.setState(
            produce((draft) => {
                draft.isFormValid = isFormValid;
                draft.isPristine = isPristine;
                draft.errors = errors;
                draft.invalid = invalid;
            })
        );
    }

    setFieldVisible(name, visible) {
        if (!this._isMounted) {
            return;
        }

        this.setState(
            produce((draft) => {
                if (visible) {
                    draft.visible[name] = true;
                } else {
                    delete draft.visible[name];
                }
            })
        );
    }

    setFieldLabel(name, label) {
        if (!this._isMounted) {
            return;
        }

        this.setState(
            produce((draft) => {
                if (isNil(label)) {
                    delete draft.labels[name];
                } else {
                    draft.labels[name] = label;
                }
            })
        );
    }

    // Default Yup/Zod messages reference fields by path ("target.fqn.table is
    // a required field"). Swap the path for the registered field label. Paths
    // that don't appear in the message (e.g. bespoke messages, schema-level
    // labels) are left untouched.
    rewriteErrorWithLabels(error, path) {
        if (isString(error)) {
            const label = this.getFieldLabel(path);
            return label ? error.split(path).join(label) : error;
        }

        if (isObject(error)) {
            const result = {};

            for (const key in error) {
                result[key] =
                    key === '__message__'
                        ? this.rewriteErrorWithLabels(error[key], path)
                        : this.rewriteErrorWithLabels(
                              error[key],
                              /^\d+$/.test(key) ? `${path}[${key}]` : `${path}.${key}`
                          );
            }

            return result;
        }

        return error;
    }

    resetField(name) {
        if (!this._isMounted) {
            return;
        }

        const initialFieldValue = getInitialFieldValue(name)(this.props);

        this.setState(
            produce((draft) => {
                set(name, false, draft.touched);
                set(name, initialFieldValue, draft.values);
                set(name, true, draft.pristine);
            }),
            this.runDebouncedValidation
        );
    }

    resetAllFields() {
        if (!this._isMounted) {
            return;
        }

        // Labels are registered by mounted fields, which won't re-register on
        // reset, so carry them over.
        this.setState(({ labels }) => ({ ...getInitialState(this.props), labels }));
    }

    async validate() {
        // Zod Schema
        if (isFunction(this.props.schema.safeParseAsync)) {
            const result = await this.props.schema.safeParseAsync(this.state.values);

            if (result.success) {
                this.setIsFormValid(true);
            } else {
                const flattened = {};
                for (const issue of result.error.issues) {
                    flattened[issue.path.join('.')] = issue.message;
                }
                this.setIsFormValid(false, flattened);
            }
        } else if (isFunction(this.props.schema.validate)) {
            // Yup Schema
            const options = {
                strict: !this.props.allowTransformation,
                abortEarly: false,
                recursive: true
            };

            this.props.schema
                .validate(this.state.values, options)
                .then(() => {
                    this.setIsFormValid(true);
                })
                .catch((error) => {
                    const flattened = {};

                    // It's possible we get an error due to a malformed schema, in which
                    // case we won't have an `inner` property.
                    if (error.inner) {
                        for (const e of error.inner) {
                            flattened[e.params.path] = e.message;
                        }
                    } else {
                        throw new Error(error);
                    }

                    this.setIsFormValid(false, flattened);
                })
                .finally(() => {
                    this.generateSummaryIfRequired();
                });
        }
    }

    generateSummaryIfRequired() {
        if (isFunction(this.props.onUpdateSummary)) {
            requestAnimationFrame(() => {
                this.props.onUpdateSummary(this.generateSummary());
            });
        }
    }

    generateSummary() {
        const { label, fields } = this.props.schema.describe();
        const entries = [];

        let totalRequired = 0;
        let totalValid = 0;

        for (const key in fields) {
            const { label, meta, optional, nullable } = fields[key];

            const required = !optional && !nullable && !meta?.optional;
            const value = this.getFieldValue(key);
            const valid = !isEmpty(value) && this.getIsFieldValid(key);

            if (required) {
                totalRequired += 1;
            }

            if (required && valid) {
                totalValid += 1;
            }

            if (meta?.summary) {
                entries.push({ key, label: meta.label ?? label, required, valid, value });
            }
        }

        return {
            label,
            fields: entries,
            isValid: this.state.isFormValid && !this.state.isPristine,
            isPristine: this.state.isPristine,
            totalRequired,
            totalValid
        };
    }

    render() {
        const { children, render, className, isSubmitting, testId, ...props } = this.props;
        const others = omit(OWN_PROPS, props);

        const context = {
            ...this.state,
            schema: this.props.schema,
            isSubmitting,
            isFormDisabled: this.props.disabled,
            isVisibleValid: areVisibleFieldsValid(
                Object.keys(this.state.visible),
                this.state.invalid
            ),
            getFieldValue: this.getFieldValue,
            getFieldError: this.getFieldError,
            getFieldSchema: this.getFieldSchema,
            setFieldValue: this.setFieldValue,
            setFieldTouched: this.setFieldTouched,
            setFieldLabel: this.setFieldLabel,
            setFieldVisible: this.setFieldVisible,
            resetField: this.resetField,
            resetAllFields: this.resetAllFields
        };

        return (
            <FormContext.Provider value={context}>
                <form
                    {...others}
                    noValidate
                    onSubmit={this.handleSubmit}
                    className={className}
                    data-testid={testId}>
                    {isFunction(render) ? render(this.state.values) : children}
                </form>
            </FormContext.Provider>
        );
    }
}

Form.propTypes = {
    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * Form content. Should contain <Field> components in order to function correctly.
     */
    children: PropTypes.node,

    /**
     * Submit handler for form, passed the current `values` object.
     */
    onSubmit: PropTypes.func,

    /**
     * Called after validation with a summary of the form fields and their status
     */
    onUpdateSummary: PropTypes.func,

    /**
     * A Yup schema used for validating the form fields. Yup is exported as part of
     * the Form package.
     */
    schema: PropTypes.oneOfType([
        PropTypes.shape({
            validate: PropTypes.func,
            cast: PropTypes.func,
            describe: PropTypes.func
        }),
        PropTypes.shape({
            safeParseAsync: PropTypes.func
        })
    ]),

    /**
     * An object containing the initial values for the fields.
     */
    initialValues: PropTypes.shape({}),

    /**
     * Whether the provided initial values should be considered valid.
     */
    isInitialValid: PropTypes.bool,

    /**
     * By default *Pristine* forms are not submittable.
     * If the initial value should be submittable, set this to true
     */
    isInitialDirty: PropTypes.bool,

    /**
     * Set this to `true` when submitting the Form to prevent further editing of the fields.
     */
    isSubmitting: PropTypes.bool,

    /**
     * Whether to reset the form values to their initial values when the form is submitted.
     */
    resetToInitialValuesOnSubmit: PropTypes.bool,

    /**
     * A function that receives the current form values and renders the form content.
     * When supplied, will be used instead of children.
     */
    render: PropTypes.func,

    /**
     * Set this to true to allow YUP to transform values before validating.
     * When we retrieve the value, we get the transformed value.
     * eg. YUP.string().trim()
     * https://github.com/jquense/yup#schemastrictenabled-boolean--false-schema
     */
    allowTransformation: PropTypes.bool,

    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Prevent the user from interacting with the field. If this prop isn't
     * set, the field can still be disabled from a containing element, for
     * example a `<fieldset>`.
     */
    disabled: PropTypes.bool
};

Form.defaultProps = {
    className: null,
    children: null,
    onSubmit: noop,
    onUpdateSummary: null,
    schema: mixed(),
    initialValues: {},
    isInitialValid: false,
    isInitialDirty: false,
    isSubmitting: false,
    resetToInitialValuesOnSubmit: false,
    render: null,
    allowTransformation: false,
    testId: null,
    disabled: false
};
